// Superadmin-only diagnostic overlay for the contact-lead / book-appointment popup.
//
// Why this exists: the contact-lead popup is reachable from MANY entry points
// (leads table, task detail, lead detail, WhatsApp, call script, …) and renders
// one of two children via ContactLeadPopupRouter — V1 OpenContactLeadPopup or
// V2 FollowUpV2. When a launch opens the WRONG outcome tab (e.g. "Spoken"
// instead of "Book"), we need to see at a glance WHICH popup is rendering,
// WHERE EXACTLY it was launched from (which screen + which button), and WHICH
// step/outcome it resolved to.
//
// This is purely additive — it changes no popup behaviour. It mirrors the
// superadmin debug pattern in AttendedChecklistV2.jsx (collapsible Accordion,
// monospace #FAFAFA box, divider border, superuser gate via
// useAuth().user.is_superuser, JSON.stringify dump, "🔧 … (superadmin only)"
// summary). Because the contact popup is a fullscreen Dialog rather than an
// in-flow card, the Accordion is anchored as a fixed bottom-right overlay so it
// stays visible above the dialog — but the visual treatment is otherwise
// identical to AttendedChecklistV2's debugOverlay. It renders nothing for
// non-superadmins.
//
// Mount it inside each popup child (V1 + V2) so it appears whichever renders,
// in the same spot with the same treatment regardless of which popup launches.

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails, Portal, Button, Divider,
} from '@mui/material';
import { useAuth } from 'src/hooks';
import { projectDentallyBookingDiagnostic } from 'src/utils/dentallyBookingDiagnostics';

export default function ContactPopupDebugBox({
    popup,
    debugSource = 'unknown',
    practiceId,
    leadId,
    taskId,
    isFollowUpV2,
    initialStep,
    activeOutcome,
    extra,
    bookingDiagnostic,
}) {
    const { user } = useAuth();
    const isSuperuser = !!user?.is_superuser;
    const [copyState, setCopyState] = useState('idle');
    const projectedBookingDiagnostic = projectDentallyBookingDiagnostic(bookingDiagnostic);
    const copyButtonLabel = {
        copied: 'Copied',
        failed: 'Copy failed',
        idle: 'Copy diagnostics',
    }[copyState];

    useEffect(() => {
        setCopyState('idle');
    }, [projectedBookingDiagnostic?.diagnostic_id]);

    if (!isSuperuser) return null;

    const handleCopyDiagnostic = async () => {
        if (!projectedBookingDiagnostic) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(projectedBookingDiagnostic, null, 2));
            setCopyState('copied');
        } catch {
            setCopyState('failed');
        }
    };

    const route = `${window.location.pathname}${window.location.search}`;

    // Surface the current step/outcome titles prominently — these reveal
    // Spoke-vs-Book mis-routing at a glance.
    const activeStepTitle =
        activeOutcome?.activeStepTitle ?? activeOutcome?.activeTab ?? null;
    const activeActionKey = activeOutcome?.activeActionKey ?? null;

    // debugSource FIRST — the entry point is the single most useful datum, so it
    // leads the dump and is also rendered as a bold standalone line above it.
    const debugInfo = {
        debugSource,
        popup,
        route,
        practiceId: practiceId ?? null,
        leadId: leadId ?? null,
        taskId: taskId ?? null,
        isFollowUpV2: !!isFollowUpV2,
        activeStepTitle,
        activeActionKey,
        initialStep: initialStep ?? null,
        activeOutcome: activeOutcome ?? null,
        ...(extra || {}),
    };

    return (
        // Portal to document.body so the overlay escapes the contact popup's
        // Dialog stacking context — otherwise a tall right-panel Dialog (z~1200)
        // rendered later in the DOM paints over the box despite its high zIndex.
        // zIndex sits above MUI modal (1300) and any nested popup.
        <Portal>
        <Box
            data-testid="contact-popup-debug-box"
            sx={{
                position: 'fixed',
                bottom: 12,
                right: 12,
                zIndex: 21000,
                width: 360,
                maxWidth: 'calc(100vw - 24px)',
                pointerEvents: 'auto',
            }}
        >
            <Accordion
                defaultExpanded
                disableGutters
                elevation={0}
                data-testid="contact-popup-debug-accordion"
                sx={{
                    bgcolor: '#FAFAFA',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    expandIcon={
                        <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: '#6e6e73', lineHeight: 1 }}>
                            ▾
                        </Typography>
                    }
                >
                    <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: '#6e6e73', fontWeight: 600 }}>
                        🔧 Contact-popup debug (superadmin only)
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            color: '#1d1d1f',
                            fontWeight: 700,
                            mb: 0.5,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                        }}
                    >
                        ↳ from: {debugSource}
                    </Typography>
                    <Box
                        component="pre"
                        sx={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            color: '#6e6e73',
                            m: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            maxHeight: 320,
                            overflow: 'auto',
                        }}
                    >
                        {JSON.stringify(debugInfo, null, 2)}
                    </Box>
                    {projectedBookingDiagnostic && (
                        <>
                            <Divider sx={{ my: 1.25 }} />
                            <Typography sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, mb: 0.5 }}>
                                Dentally booking diagnostic
                            </Typography>
                            <Box
                                component="pre"
                                data-testid="dentally-booking-diagnostic"
                                sx={{
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    color: '#6e6e73',
                                    m: 0,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    maxHeight: 220,
                                    overflow: 'auto',
                                }}
                            >
                                {JSON.stringify(projectedBookingDiagnostic, null, 2)}
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCopyDiagnostic}
                                sx={{ mt: 1, textTransform: 'none', fontFamily: 'monospace', fontSize: 11 }}
                            >
                                {copyButtonLabel}
                            </Button>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
        </Portal>
    );
}

ContactPopupDebugBox.propTypes = {
    popup: PropTypes.string.isRequired,
    debugSource: PropTypes.string,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isFollowUpV2: PropTypes.bool,
    initialStep: PropTypes.any,
    activeOutcome: PropTypes.any,
    extra: PropTypes.object,
    bookingDiagnostic: PropTypes.object,
};
